/** Черновик мастера создания теста: живёт в браузере до кнопки «Сохранить тест» (макет v6). */
import type { ComposeItem, ComposeRequest, QuestionKind, TestBlock } from './model';
import type { PickedRow } from './selectionStore';

const KEY = 'tw.wizard';

/** Больше вопросов в одном новом тесте сервер не принимает (`POST /admin/tests/compose`). */
export const MAX_ITEMS = 250;

export interface WizardSourceItem {
  kind: 'source';
  id: number;
  text: string;
  type: QuestionKind;
}

export interface WizardNewItem {
  kind: 'new';
  text: string;
  image_url?: string;
  answers: string[];
  answer_image_urls?: string[];
  rightIndex: number;
}

export type WizardItem = WizardSourceItem | WizardNewItem;

export interface WizardDraft {
  title: string;
  minutes: number;
  block: TestBlock;
  description: string;
  subgroup: string;
  who: 'nobody' | 'all';
  items: WizardItem[];
}

export const emptyDraft = (): WizardDraft => ({
  title: '',
  minutes: 30,
  block: 'products',
  description: '',
  subgroup: '',
  who: 'nobody',
  items: [],
});

/** Элемент черновика из хранилища берётся, только если у него верная форма (хранилище могло остаться от прежней сборки). */
function cleanItem(value: unknown): WizardItem | null {
  if (!value || typeof value !== 'object') return null;
  const { kind, id, text, type, image_url, answers, answer_image_urls, rightIndex } = value as Record<string, unknown>;
  if (kind === 'source' && typeof id === 'number' && typeof text === 'string' && typeof type === 'string') {
    return { kind: 'source', id, text, type: type as QuestionKind };
  }
  if (
    kind === 'new'
    && typeof text === 'string'
    && typeof rightIndex === 'number'
    && Array.isArray(answers)
    && answers.every((answer) => typeof answer === 'string')
  ) {
    const imageUrl = typeof image_url === 'string' ? image_url.trim() : '';
    const answerImageUrls = Array.isArray(answer_image_urls)
      ? answers.map((_, index) => typeof answer_image_urls[index] === 'string' ? answer_image_urls[index].trim() : '')
      : [];
    return {
      kind: 'new', text, answers: answers as string[], rightIndex,
      ...(imageUrl ? { image_url: imageUrl } : {}),
      ...(answerImageUrls.some(Boolean) ? { answer_image_urls: answerImageUrls } : {}),
    };
  }
  return null;
}

export function loadDraft(): WizardDraft {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return emptyDraft();
    const parsed = JSON.parse(raw) as Record<string, unknown> | null;
    if (!parsed || typeof parsed !== 'object') return emptyDraft();
    const base = emptyDraft();
    const { title, minutes, block, description, subgroup, who, items } = parsed;
    return {
      title: typeof title === 'string' ? title : base.title,
      minutes: typeof minutes === 'number' && minutes >= 1 && minutes <= 300 ? minutes : base.minutes,
      block: block === 'vehicles' ? 'vehicles' : base.block,
      description: typeof description === 'string' ? description : base.description,
      subgroup: typeof subgroup === 'string' ? subgroup : base.subgroup,
      who: who === 'all' ? 'all' : base.who,
      items: Array.isArray(items)
        ? items.flatMap((entry): WizardItem[] => {
          const clean = cleanItem(entry);
          return clean ? [clean] : [];
        })
        : [],
    };
  } catch {
    return emptyDraft();
  }
}

export function saveDraft(draft: WizardDraft): void {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(draft));
  } catch {
    /* хранилище недоступно: черновик живёт только на экране */
  }
}

export function clearDraft(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* см. выше */
  }
}

/** Есть ли в черновике то, что жалко потерять молча. */
export const hasContent = (draft: WizardDraft): boolean =>
  draft.title.trim() !== '' || draft.description.trim() !== '' || draft.items.length > 0;

/** Добавить готовые вопросы из библиотеки; уже добавленные пропускаются. */
export function addSourceItems(draft: WizardDraft, rows: PickedRow[]): WizardDraft {
  const have = new Set(
    draft.items.filter((item): item is WizardSourceItem => item.kind === 'source').map((item) => item.id),
  );
  const fresh: WizardSourceItem[] = [];
  rows.forEach((row) => {
    if (have.has(row.question_id)) return;
    have.add(row.question_id);
    fresh.push({ kind: 'source', id: row.question_id, text: row.text, type: row.type });
  });
  return { ...draft, items: [...draft.items, ...fresh] };
}

export function moveItem<T>(items: T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (index < 0 || index >= items.length || target < 0 || target >= items.length) return items;
  const next = items.slice();
  const held = next[index];
  next[index] = next[target];
  next[target] = held;
  return next;
}

export function removeItem<T>(items: T[], index: number): T[] {
  return items.filter((_, position) => position !== index);
}

/** Варианты различаются текстом или фото; текст сравнивается без регистра и пробелов по краям. */
export function manualHasRepeats(answers: string[], imageUrls: string[] = []): boolean {
  const filled = answers
    .map((answer, index) => ({ text: answer.trim().toLowerCase(), image: imageUrls[index]?.trim() || '' }))
    .filter((answer) => answer.text !== '')
    .map((answer) => JSON.stringify([answer.text, answer.image]));
  return new Set(filled).size !== filled.length;
}

/** «Написать самому»: нужен текст, не меньше двух разных вариантов и непустой правильный. */
export function manualIsValid(text: string, answers: string[], rightIndex: number, imageUrls?: string[]): boolean {
  const filled = answers.map((answer) => answer.trim());
  return text.trim() !== ''
    && filled.filter(Boolean).length >= 2
    && Boolean(filled[rightIndex])
    && !imageUrls?.some((url, index) => url.trim() !== '' && !filled[index])
    && !manualHasRepeats(answers, imageUrls);
}

export function toComposeRequest(draft: WizardDraft): ComposeRequest {
  const items: ComposeItem[] = draft.items.map((item) => {
    if (item.kind === 'source') return { source_question_id: item.id };
    return {
      new_question: {
        text: item.text.trim(),
        type: 'single_choice' as const,
        ...(item.image_url?.trim() ? { image_url: item.image_url.trim() } : {}),
        answers: item.answers
          .map((answer, index) => {
            const imageUrl = item.answer_image_urls?.[index]?.trim();
            return { text: answer.trim(), is_correct: index === item.rightIndex, order: index,
              ...(imageUrl ? { image_url: imageUrl } : {}) };
          })
          .filter((answer) => answer.text !== ''),
      },
    };
  });
  return {
    title: draft.title.trim(),
    description: draft.description.trim() || null,
    block: draft.block,
    subgroup: draft.subgroup || null,
    time_limit_minutes: draft.minutes,
    items,
    publish: draft.who === 'all',
  };
}
